import { supabase } from '../lib/supabase';

/**
 * Topics Service — queries Nave's Topical Bible data for a given verse.
 * Tables: nave_topics, nave_entries, nave_verses, nave_cross_references
 */
export const topicsService = {
  /**
   * Get all topics (with nested entries and verses) for a specific verse.
   *
   * Strategy:
   *  1. Find nave_verses rows that match book/chapter/verse → gives us entry_ids + topic_ids
   *  2. Fetch only the nave_entries rows whose id is in those entry_ids
   *  3. Fetch topic names for the matched topic_ids
   *  4. Fetch ALL nave_verses for those entry_ids (so the subtopic shows all related verses)
   *  5. Build nested structure: topic → entry (subtopic) → verses
   *
   * @param {string} book  - e.g. "genesis"
   * @param {number} chapter
   * @param {number} verse
   * @returns {{ data: TopicGroup[], error: object|null }}
   */
  async getTopicsForVerse(book, chapter, verse) {
    try {
      if (!book || !chapter || !verse) {
        return { data: [], error: { message: 'Book, chapter, and verse are required.' } };
      }

      // Normalize book name to title-case to match stored values
      const normalizedBook = book?.charAt(0)?.toUpperCase() + book?.slice(1)?.toLowerCase();

      // ── Step 1: Find nave_verses rows that match this book/chapter/verse ──
      const { data: matchingVerses, error: versesError } = await supabase?.from('nave_verses')?.select('id, entry_id, topic_id, book, chapter, verse_start, verse_end')?.ilike('book', normalizedBook)?.eq('chapter', chapter)?.lte('verse_start', verse)?.or(`verse_end.is.null,verse_end.gte.${verse}`);

      if (versesError) {
        console.error('Error fetching nave_verses:', versesError);
        return { data: [], error: versesError };
      }

      if (!matchingVerses || matchingVerses?.length === 0) {
        return { data: [], error: null };
      }

      // Collect unique entry_ids and topic_ids from the matching verses
      const entryIds = [...new Set(matchingVerses.map(v => v.entry_id).filter(Boolean))];
      const topicIds = [...new Set(matchingVerses.map(v => v.topic_id).filter(Boolean))];

      if (entryIds?.length === 0 && topicIds?.length === 0) {
        return { data: [], error: null };
      }

      // ── Step 2: Fetch only the entries (subtopics) linked to this verse ──
      let entriesData = [];
      if (entryIds?.length > 0) {
        const { data: fetchedEntries, error: entriesError } = await supabase?.from('nave_entries')?.select('id, topic_id, section, subtopic, display_order')?.in('id', entryIds)?.order('display_order');

        if (entriesError) {
          console.error('Error fetching nave_entries:', entriesError);
          return { data: [], error: entriesError };
        }
        entriesData = fetchedEntries || [];
      }

      // Collect all topic_ids from entries (may differ from matchingVerses topic_ids)
      const allTopicIds = [...new Set([
        ...topicIds,
        ...entriesData.map(e => e.topic_id).filter(Boolean),
      ])];

      if (allTopicIds?.length === 0) {
        return { data: [], error: null };
      }

      // ── Step 3: Fetch topic names ──
      const { data: topicsData, error: topicsError } = await supabase?.from('nave_topics')?.select('id, slug, name')?.in('id', allTopicIds)?.order('name');

      if (topicsError) {
        console.error('Error fetching nave_topics:', topicsError);
        return { data: [], error: topicsError };
      }

      // ── Step 4: Fetch ALL verses for the matched entry_ids ──
      let allEntryVerses = [];
      if (entryIds?.length > 0) {
        const { data: entryVersesData, error: entryVersesError } = await supabase?.from('nave_verses')?.select('id, entry_id, topic_id, book, chapter, verse_start, verse_end, display_order')?.in('entry_id', entryIds)?.order('display_order');

        if (entryVersesError) {
          console.error('Error fetching entry verses:', entryVersesError);
        } else {
          allEntryVerses = entryVersesData || [];
        }
      }

      // ── Step 5: Fetch cross-references for these topics ──
      const { data: crossRefsData } = await supabase?.from('nave_cross_references')?.select('id, from_topic_id, to_topic_slug, context, ref_type')?.in('from_topic_id', allTopicIds);

      // ── Step 6: Build nested structure: topic → entries → verses ──
      const topicMap = new Map();
      (topicsData || [])?.forEach(topic => {
        topicMap?.set(topic?.id, {
          id: topic?.id,
          slug: topic?.slug,
          name: topic?.name,
          entries: [],
          crossRefs: (crossRefsData || [])?.filter(cr => cr?.from_topic_id === topic?.id),
        });
      });

      // Group entries by topic
      const entryMap = new Map();
      entriesData?.forEach(entry => {
        const topic = topicMap?.get(entry?.topic_id);
        if (topic) {
          const entryObj = {
            id: entry?.id,
            section: entry?.section,
            subtopic: entry?.subtopic,
            displayOrder: entry?.display_order,
            verses: [],
          };
          topic?.entries?.push(entryObj);
          entryMap?.set(entry?.id, entryObj);
        }
      });

      // Attach verses to their entries
      allEntryVerses?.forEach(v => {
        const entry = entryMap?.get(v?.entry_id);
        if (entry) {
          entry?.verses?.push({
            id: v?.id,
            book: v?.book,
            chapter: v?.chapter,
            verseStart: v?.verse_start,
            verseEnd: v?.verse_end,
            displayOrder: v?.display_order,
          });
        }
      });

      // Only include topics that have at least one entry
      const result = Array.from(topicMap?.values())?.filter(t => t?.entries?.length > 0);

      return { data: result, error: null };
    } catch (err) {
      console.error('Network error in getTopicsForVerse:', err);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  /**
   * Fetch verse text for a given book/chapter/verse in the user's selected version.
   * Falls back to KJV if the version is not found.
   */
  async getVerseText(book, chapter, verseNum, versionShortName = 'KJV') {
    try {
      const { data, error } = await supabase?.from('bible_verses')?.select(`
          text,
          bible_versions!inner(short_name),
          bible_chapters!inner(
            chapter_number,
            bible_books!inner(name)
          )
        `)?.ilike('bible_chapters.bible_books.name', book)?.eq('bible_chapters.chapter_number', chapter)?.eq('verse_number', verseNum)?.ilike('bible_versions.short_name', versionShortName)?.maybeSingle();

      if (error || !data) {
        if (versionShortName?.toUpperCase() !== 'KJV') {
          return this.getVerseText(book, chapter, verseNum, 'KJV');
        }
        return null;
      }

      return data?.text || null;
    } catch {
      return null;
    }
  },
};
